import React, { useEffect } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { useCreateVendor, useUpdateVendor } from '~/services/vendor';
import { CreateVendorDto } from '~/services/vendor/vendor.types';
import { TVendor } from '~/types';

interface VendorModalProps {
  open: boolean;
  onCancel: () => void;
  vendor?: TVendor | null;
  onSuccess: () => void;
}

const serviceOptions = [
  'Freight Forwarding',
  'Warehousing',
  'Fine Art Shipping',
  'Customs Clearance',
  'Installation Services',
  'Packing & Crating',
  'Climate Control',
  'Insurance',
  'Transportation',
  'Storage',
];

export const VendorModal: React.FC<VendorModalProps> = ({
  open,
  onCancel,
  vendor,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();

  const isEditing = !!vendor;

  useEffect(() => {
    if (open) {
      if (vendor) {
        form.setFieldsValue({
          name: vendor.name,
          description: vendor.description,
          email: vendor.email,
          phone: vendor.phone,
          contact: vendor.contact,
          services: vendor.services || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, vendor, form]);

  const handleSubmit = async (values: CreateVendorDto) => {
    try {
      if (isEditing && vendor) {
        await updateVendor.mutateAsync({
          vendorId: vendor.id,
          payload: values,
        });
        message.success('Vendor updated successfully');
      } else {
        await createVendor.mutateAsync(values);
        message.success('Vendor created successfully');
      }
      onSuccess();
      onCancel();
    } catch {
      message.error(`Failed to ${isEditing ? 'update' : 'create'} vendor`);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Vendor' : 'Add New Vendor'}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={createVendor.isPending || updateVendor.isPending}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4"
      >
        <Form.Item
          name="name"
          label="Vendor Name"
          rules={[{ required: true, message: 'Please enter vendor name' }]}
        >
          <Input placeholder="Enter vendor name" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
        >
          <Input.TextArea
            rows={3}
            placeholder="Enter vendor description"
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="contact"
            label="Contact Person"
          >
            <Input placeholder="Contact name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="vendor@company.com" />
          </Form.Item>
        </div>

        <Form.Item
          name="phone"
          label="Phone"
        >
          <Input placeholder="Phone number" />
        </Form.Item>

        <Form.Item
          name="services"
          label="Services Offered"
        >
          <Select
            mode="multiple"
            placeholder="Select services"
            options={serviceOptions.map(service => ({
              label: service,
              value: service,
            }))}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
